import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "./searchable-select.scss";
import DownArrowIcon from "../../SVG/down-arrow";
import UpArrowIcon from "../../SVG/up-arrow";
import TickIcon from "../../SVG/tick";
import { usePortal } from "../../hooks/use-portal";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  required?: boolean;
  id: string;
  onOptionChange: (data: { name: string; value: string }) => void;
  error?: string;
  label: string;
  placeholder: string;
  hint?: string;
  defaultValue?: string;
  options: SearchableSelectOption[];
  value?: string;
  resetValue?: boolean;
  shouldOmitPrevSelectedValueOnReset?: boolean;
  /** Placeholder for the search input inside the dropdown. @default "Search..." */
  searchPlaceholder?: string;
  /** Message shown when search yields no results. @default "No results found" */
  noResultsMessage?: string;
  disabled?: boolean;
  /**
   * When true, the options dropdown is rendered via `usePortal`.
   * Mounts into `targetRef` when provided, otherwise `document.body`.
   * @default false
   */
  usePortal?: boolean;
  /**
   * Portal mount element when `usePortal` is true. Coordinates are computed
   * relative to this container (`position: absolute`). The container should
   * have `position: relative`.
   */
  targetRef?: React.RefObject<HTMLElement | null>;
}

export function SearchableSelect(props: SearchableSelectProps) {
  const {
    searchPlaceholder = "Search...",
    noResultsMessage = "No results found",
    disabled = false,
    usePortal: shouldUsePortal = false,
    targetRef,
  } = props;

  const [selectedValue, setSelectedValue] = useState<SearchableSelectOption>({
    value: "",
    label: "",
  });
  const [openOptions, setOpenOptions] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [position, setPosition] = useState<"bottom" | "top" | "">("bottom");
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  const selectValueRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const getPortalContainer = useCallback(
    () => targetRef?.current ?? null,
    [targetRef]
  );
  const renderInPortal = usePortal(getPortalContainer);

  const updatePortalPosition = useCallback(() => {
    if (!selectValueRef.current) return;
    const triggerRect = selectValueRef.current.getBoundingClientRect();
    const gap = 4;

    if (targetRef?.current) {
      const containerRect = targetRef.current.getBoundingClientRect();
      const scrollTop = targetRef.current.scrollTop;
      const scrollLeft = targetRef.current.scrollLeft;
      const spaceBelow = containerRect.bottom - triggerRect.bottom - gap;
      const spaceAbove = triggerRect.top - containerRect.top - gap;
      const openAbove =
        spaceBelow < containerRect.height * 0.45 && spaceAbove > spaceBelow;

      setPosition(openAbove ? "top" : "bottom");
      setPortalStyle({
        position: "absolute",
        left: triggerRect.left - containerRect.left + scrollLeft,
        width: triggerRect.width,
        zIndex: 99999,
        ...(openAbove
          ? {
              bottom:
                containerRect.bottom - triggerRect.top + scrollTop + gap,
            }
          : {
              top: triggerRect.bottom - containerRect.top + scrollTop + gap,
            }),
      });
    } else {
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const openAbove = spaceBelow < window.innerHeight * 0.45;

      setPosition(openAbove ? "top" : "bottom");
      setPortalStyle({
        position: "fixed",
        left: triggerRect.left,
        width: triggerRect.width,
        zIndex: 99999,
        ...(openAbove
          ? { bottom: window.innerHeight - triggerRect.top + gap }
          : { top: triggerRect.bottom + gap }),
      });
    }
  }, [targetRef]);

  useEffect(() => {
    if (props.value) {
      findValue(props.value);
    } else if (props.defaultValue) {
      findValue(props.defaultValue);
    }
    setError(props.error ?? "");
  }, [props.error, props.defaultValue, props.value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        selectValueRef.current &&
        !selectValueRef.current.contains(event.target as Node) &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target as Node)
      ) {
        setOpenOptions(false);
        setSearchQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectValueRef, optionsRef]);

  useEffect(() => {
    if (props.shouldOmitPrevSelectedValueOnReset) {
      setSelectedValue({ value: "", label: "" });
    } else {
      if (props.defaultValue) {
        findValue(props.defaultValue);
      } else {
        setSelectedValue({ value: "", label: "" });
      }
    }
  }, [props.resetValue]);

  useEffect(() => {
    if (openOptions) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [openOptions]);

  useEffect(() => {
    if (!openOptions || !shouldUsePortal) return;

    const handleScrollOrResize = () => updatePortalPosition();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [openOptions, shouldUsePortal, updatePortalPosition]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return props.options;
    const q = searchQuery.trim().toLowerCase();
    return props.options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.value.toLowerCase().includes(q)
    );
  }, [searchQuery, props.options]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (openOptions) {
      setOpenOptions(false);
      setPosition("");
      setSearchQuery("");
    } else {
      if (selectValueRef.current) {
        if (shouldUsePortal) {
          updatePortalPosition();
        } else {
          const bottom =
            selectValueRef.current.getBoundingClientRect().bottom || 0;
          const fullHeight = window.innerHeight;
          const availableSpaceBelow = fullHeight - bottom;
          const openPosition =
            availableSpaceBelow < fullHeight * 0.45 ? "top" : "bottom";
          setPosition(openPosition);
        }
        setOpenOptions(true);
      }
    }
  }, [disabled, openOptions, shouldUsePortal, updatePortalPosition]);

  const handleOptionSelect = useCallback(
    (option: SearchableSelectOption) => {
      setSelectedValue(option);
      setOpenOptions(false);
      setSearchQuery("");
      props.onOptionChange({
        name: props.id,
        value: option.value,
      });
    },
    [props.id, props.onOptionChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        setOpenOptions(false);
        setSearchQuery("");
      }
    },
    []
  );

  const findValue = (value: string) => {
    const option = props.options.find((opt) => opt.value === value);
    if (option) {
      setSelectedValue(option);
    }
  };

  const optionsDropdown = openOptions ? (
    <div
      className={[
        "searchable-options",
        position === "bottom" ? "bottom" : "top",
        shouldUsePortal ? "portal" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      ref={optionsRef}
      style={shouldUsePortal ? portalStyle : undefined}
    >
      <div className="search-box">
        <SearchIcon />
        <input
          ref={searchInputRef}
          type="text"
          className="search-input"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          autoComplete="off"
          aria-label="Search options"
        />
        {searchQuery.length > 0 && (
          <button
            type="button"
            className="search-clear"
            aria-label="Clear search"
            onClick={() => {
              setSearchQuery("");
              searchInputRef.current?.focus();
            }}
          >
            <ClearIcon />
          </button>
        )}
      </div>
      <div className="options-list">
        {filteredOptions.length === 0 ? (
          <div className="no-results">{noResultsMessage}</div>
        ) : (
          filteredOptions.map((option) => (
            <div
              key={option.value}
              className={
                selectedValue.value === option.value
                  ? "option selected"
                  : "option"
              }
              onClick={() => handleOptionSelect(option)}
            >
              {option.label}
              {selectedValue.value === option.value && <TickIcon />}
            </div>
          ))
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="searchable-select-container">
      <div
        className={`searchable-select ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}
      >
        <div className="label">
          {props.label}{" "}
          {props.required && <span className="required"> *</span>}
        </div>
        <div
          className={openOptions ? "selected-text open" : "selected-text"}
          onClick={handleClick}
          ref={selectValueRef}
        >
          {selectedValue.label === "" ? (
            <div className="placeholder">{props.placeholder}</div>
          ) : (
            <div className="selected-value">{selectedValue.label}</div>
          )}
          {openOptions ? <UpArrowIcon /> : <DownArrowIcon />}
        </div>
        {props.hint && <div className="select-hint">{props.hint}</div>}
        {error && <div className="error-message">{error}</div>}
        {shouldUsePortal
          ? openOptions
            ? renderInPortal(optionsDropdown)
            : null
          : optionsDropdown}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M6.417 11.083a4.667 4.667 0 1 0 0-9.333 4.667 4.667 0 0 0 0 9.333ZM12.25 12.25l-2.534-2.534"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M9 3L3 9M3 3l6 6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
